import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, fmtEUR } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Info, UserX } from "lucide-react";
import { useLists } from "@/hooks/useLists";
import { useAuth } from "@/context/AuthContext";

const DEFAULT_ROLES = ["barista", "cameriere", "cucina", "lavapiatti", "responsabile sala", "responsabile bar", "manager"];
const EMPTY = {
  first_name: "", last_name: "", role: "barista", phone: "", email: "",
  hire_date: "", end_date: "", contract_type: "Indeterminato",
  ccnl: "", contract_level: "",
  hours_week: "", hourly_cost: "", monthly_salary: "",
  active: true, notes: "",
};

export default function Employees() {
  const { lists } = useLists();
  const { can } = useAuth();
  // FASE 7.4 STEP F.1 — employee mutations require employees.write.
  // Cost fields UI is gated on employees.read_cost (commercialista may see costs but not edit).
  const canWrite = can("employees.write");
  const canReadCost = can("employees.read_cost");
  const ROLES = lists.employee_roles?.length ? lists.employee_roles : DEFAULT_ROLES;
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => { const { data } = await api.get("/employees"); setList(data); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (e) => {
    setEditing(e);
    setForm({ ...EMPTY, ...e,
      end_date: e.end_date || "",
      hourly_cost: e.hourly_cost ?? "",
      monthly_salary: e.monthly_salary ?? "",
      hours_week: e.hours_week ?? "",
      active: e.active !== false });
    setOpen(true);
  };

  const num = (v) => v === "" || v === null ? null : Number(v);
  const trimOrNull = (s) => (s && s.trim()) ? s.trim() : null;

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      role: form.role,
      contract_type: trimOrNull(form.contract_type),
      ccnl: trimOrNull(form.ccnl),
      contract_level: trimOrNull(form.contract_level),
      phone: trimOrNull(form.phone),
      email: trimOrNull(form.email),
      hire_date: trimOrNull(form.hire_date),
      end_date: trimOrNull(form.end_date),
      hours_week: num(form.hours_week),
      hourly_cost: num(form.hourly_cost),
      monthly_salary: num(form.monthly_salary),
      active: !!form.active,
      notes: trimOrNull(form.notes),
    };
    try {
      if (editing) await api.put(`/employees/${editing.id}`, payload);
      else await api.post("/employees", payload);
      toast.success(editing ? "Dipendente aggiornato" : "Dipendente aggiunto");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare il dipendente? Se ha turni collegati, considera invece 'Disattiva'.")) return;
    try { await api.delete(`/employees/${id}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const activeEmps = list.filter((e) => e.active !== false);
  const withMonthlyCost = activeEmps.filter((e) => Number(e.monthly_salary || 0) > 0);
  const totalCost = withMonthlyCost.reduce((s, e) => s + Number(e.monthly_salary || 0), 0);
  const coverageComplete = activeEmps.length > 0 && withMonthlyCost.length === activeEmps.length;

  return (
    <div data-testid="employees-page">
      <PageHeader eyebrow="Team" title="Personale"
        subtitle={
          !canReadCost
            ? (activeEmps.length === 0 ? "Nessun dipendente attivo configurato" : `${activeEmps.length} dipendenti attivi`)
            : (activeEmps.length === 0
              ? "Nessun dipendente attivo configurato"
              : `Costo aziendale mensile: ${fmtEUR(totalCost)} · copertura ${withMonthlyCost.length}/${activeEmps.length}${coverageComplete ? "" : " (dati incompleti)"}`)
        }
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            {canWrite && (
            <DialogTrigger asChild>
              <Button data-testid="new-employee-btn" onClick={openNew} className="bg-neutral-900 text-[#FDFBF7]">
                <Plus size={14} strokeWidth={1.5} /> Nuovo
              </Button>
            </DialogTrigger>
            )}
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-serif">{editing ? "Modifica dipendente" : "Nuovo dipendente"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-3">
                <div><Label>Nome</Label><Input data-testid="emp-first" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
                <div><Label>Cognome</Label><Input data-testid="emp-last" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
                <div>
                  <Label>Ruolo professionale</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="emp-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="text-[11px] text-neutral-500 mt-1">Il ruolo NON determina automaticamente l'area di lavoro dei turni (impostabile per singolo turno).</p>
                </div>
                <div><Label>Tipo contratto</Label><Input value={form.contract_type || ""} onChange={(e) => setForm({ ...form, contract_type: e.target.value })} placeholder="Indeterminato / Determinato / Stagionale / Apprendistato" /></div>
                <div><Label>CCNL</Label><Input value={form.ccnl || ""} onChange={(e) => setForm({ ...form, ccnl: e.target.value })} placeholder="Es. Pubblici esercizi" /></div>
                <div><Label>Livello</Label><Input value={form.contract_level || ""} onChange={(e) => setForm({ ...form, contract_level: e.target.value })} placeholder="Es. 4° livello" /></div>
                <div><Label>Telefono</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label>Email</Label><Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Data assunzione</Label><Input type="date" value={form.hire_date || ""} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} /></div>
                <div><Label>Data fine (se cessato)</Label><Input type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
                <div><Label>Ore/settimana</Label><Input type="number" min="0" step="0.5" value={form.hours_week} onChange={(e) => setForm({ ...form, hours_week: e.target.value })} placeholder="es. 40" /></div>
                {canReadCost && (
                <div>
                  <Label>Costo orario aziendale (€)</Label>
                  <Input data-testid="emp-hourly-cost" type="number" min="0" step="0.01" value={form.hourly_cost} onChange={(e) => setForm({ ...form, hourly_cost: e.target.value })} placeholder="Full employer cost" disabled={!canWrite} />
                  <p className="text-[11px] text-neutral-500 mt-1">Comunicato dal commercialista. Non usare stime automatiche.</p>
                </div>
                )}
                {canReadCost && (
                <div>
                  <Label>Costo aziendale mensile (€)</Label>
                  <Input data-testid="emp-monthly-cost" type="number" min="0" step="0.01" value={form.monthly_salary} onChange={(e) => setForm({ ...form, monthly_salary: e.target.value })} placeholder="Full employer cost mensile" disabled={!canWrite} />
                  <p className="text-[11px] text-neutral-500 mt-1">Indicatore budget. Non entra nel break-even. Se vuoto → coverage parziale.</p>
                </div>
                )}
                <div className="col-span-2 flex items-center gap-2">
                  <input id="emp-active" type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="emp-active" />
                  <Label htmlFor="emp-active">Attivo (deseleziona per cessazione/pausa)</Label>
                </div>
                <div className="col-span-2"><Label>Note</Label><Input value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <div className="col-span-2 text-[11px] text-neutral-500 bg-[#F3EDE1] rounded p-2 flex gap-2">
                  <Info size={12} className="mt-0.5 flex-shrink-0" />
                  <span>Costo orario e mensile hanno finalità diverse (turni vs indicatore budget): il sistema non li somma. Se mancano, il break-even resta trasparente ("insufficient_data") invece di essere stimato.</span>
                </div>
                <DialogFooter className="col-span-2">
                  {canWrite && (
                  <Button data-testid="emp-submit" type="submit" className="bg-neutral-900 text-[#FDFBF7]">
                    {editing ? "Salva modifiche" : "Salva"}
                  </Button>
                  )}
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        } />

      {!coverageComplete && activeEmps.length > 0 && canReadCost && (
        <div className="mb-6 border border-amber-200 bg-amber-50 text-amber-900 rounded-lg p-3 text-sm flex gap-2">
          <Info size={14} className="mt-0.5" />
          <div>
            <b>Copertura costo mensile parziale.</b> {activeEmps.length - withMonthlyCost.length} dipendente/i senza costo aziendale mensile configurato — il totale mostrato non rappresenta il costo completo.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.length === 0 && (
          <div className="col-span-full text-sm text-neutral-500 border border-dashed border-[#E2D9C8] rounded-lg p-8 text-center">
            Nessun dipendente. Clicca <b>Nuovo</b> per iniziare.
          </div>
        )}
        {list.map((e) => (
          <button key={e.id} type="button" onClick={() => openEdit(e)} className={`text-left border rounded-lg p-5 bg-white hover:border-neutral-400 transition-colors ${e.active === false ? "border-neutral-300 opacity-70" : "border-[#E2D9C8]"}`} data-testid={`emp-${e.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="font-serif text-xl">{e.first_name} {e.last_name}</div>
                <div className="label-eyebrow mt-1">{e.role}</div>
              </div>
              <div className="flex items-center gap-2">
                {e.active === false && <UserX size={14} className="text-neutral-400" />}
                {canWrite && (
                  <button onClick={(ev) => { ev.stopPropagation(); del(e.id); }} className="text-neutral-400 hover:text-red-700"><Trash2 size={14} strokeWidth={1.5} /></button>
                )}
              </div>
            </div>
            <div className="mt-4 space-y-1 text-sm">
              {e.email && <div className="text-neutral-600">{e.email}</div>}
              {e.phone && <div className="text-neutral-600">{e.phone}</div>}
              <div className="text-neutral-500 text-xs">
                {e.contract_type || "—"}{e.contract_level ? ` · ${e.contract_level}` : ""}{e.hours_week ? ` · ${e.hours_week}h/sett` : ""}
              </div>
              {e.end_date && <div className="text-[11px] text-neutral-500">Fine: {e.end_date}</div>}
            </div>
            {canReadCost && (
            <div className="mt-3 pt-3 border-t border-[#E2D9C8] flex justify-between">
              <div>
                <div className="label-eyebrow">Costo orario</div>
                <div className="font-medium">{e.hourly_cost > 0 ? fmtEUR(e.hourly_cost) : <span className="text-neutral-300 text-sm">non configurato</span>}</div>
              </div>
              <div className="text-right">
                <div className="label-eyebrow">Costo mensile</div>
                <div className="font-medium">{e.monthly_salary > 0 ? fmtEUR(e.monthly_salary) : <span className="text-neutral-300 text-sm">non configurato</span>}</div>
              </div>
            </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
